import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BigButton } from '../components/BigButton';
import {
  type WorksheetRecord,
  type SubjectGroup,
  deleteWorksheet,
  formatDate,
  formatDateTime,
  groupBySubjectAndMonth,
  listWorksheets,
} from '../lib/worksheets';
import { canvasToPdfBlob } from '../lib/export';
import { IconCamera, IconClock, IconDoc, IconShare, IconTrash } from '../components/Icons';

// History of saved worksheets, organised into smart folders.
//
// Top of the screen: the most recent worksheet (big card).
// Then: collapsible folders per subject (Maths, English, Science, etc.) containing
// month sections (May 2026, April 2026, etc.) with worksheet cards inside.
// Subjects and months only appear if they contain worksheets.

export function HistoryScreen() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<WorksheetRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const list = await listWorksheets();
      if (!cancelled) {
        setRecords(list);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  async function handleDelete(id: string) {
    if (!confirm('Delete this worksheet permanently? This cannot be undone.')) return;
    await deleteWorksheet(id);
    setReloadKey((k) => k + 1);
  }

  const groups = groupBySubjectAndMonth(records);
  const mostRecent = records[0];

  return (
    <AppShell title="History">
      <div className="px-5 pt-2 pb-6">
        <section className="py-4">
          <h1 className="text-2xl font-medium text-ink">History</h1>
          <p className="mt-2 text-sm text-ink/60 leading-relaxed">
            Every worksheet you finish is saved here, sorted by subject and month.
          </p>
        </section>

        {loading ? (
          <p className="text-sm text-ink/50 text-center py-12">Loading...</p>
        ) : records.length === 0 ? (
          <EmptyState onStart={() => navigate('/app/capture')} />
        ) : (
          <>
            {mostRecent && (
              <section className="mb-6">
                <p className="text-xs uppercase tracking-wider text-ink/40 mb-2 px-1">Most recent</p>
                <WorksheetCard record={mostRecent} primary onDelete={() => handleDelete(mostRecent.id)} />
              </section>
            )}

            <section className="mb-6">
              <p className="text-xs uppercase tracking-wider text-ink/40 mb-2 px-1">All worksheets</p>
              <div className="space-y-4">
                {groups.map((group) => (
                  <SubjectFolder key={group.subject} group={group} onDelete={handleDelete} />
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </AppShell>
  );
}

function EmptyState({ onStart }: { onStart: () => void }) {
  return (
    <div className="rounded-3xl bg-white/70 border border-ink/5 p-8 text-center flex flex-col items-center gap-4">
      <span className="w-14 h-14 rounded-full bg-accentSoft text-accent grid place-items-center">
        <IconClock size={26} />
      </span>
      <div>
        <p className="text-sm font-medium text-ink">Nothing here yet</p>
        <p className="mt-1 text-xs text-ink/50 max-w-xs leading-relaxed">
          Once you finish a worksheet, we save it here. Sorted by subject. Tap any card to download
          the PDF again.
        </p>
      </div>
      <div className="w-full max-w-xs mt-2">
        <BigButton onClick={onStart}>
          <span className="inline-flex items-center gap-2 justify-center">
            <IconCamera size={18} /> New worksheet
          </span>
        </BigButton>
      </div>
    </div>
  );
}

function SubjectFolder({
  group,
  onDelete,
}: {
  group: SubjectGroup;
  onDelete: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const total = group.months.reduce((n, m) => n + m.worksheets.length, 0);

  return (
    <article className="rounded-2xl border border-ink/5 bg-white/70 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/5 transition"
      >
        <span className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl bg-accentSoft text-accent grid place-items-center">
            <SubjectIcon subject={group.subject} />
          </span>
          <span className="text-left">
            <span className="block text-sm font-medium text-ink">{group.subjectLabel}</span>
            <span className="block text-xs text-ink/50">
              {total} worksheet{total === 1 ? '' : 's'}
            </span>
          </span>
        </span>
        <span className={`text-ink/40 transition ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          {group.months.map((month) => (
            <div key={month.monthSort}>
              <p className="text-[10px] uppercase tracking-widest text-ink/40 mb-2 mt-2">
                {month.monthLabel}
              </p>
              <ul className="space-y-2">
                {month.worksheets.map((r) => (
                  <li key={r.id}>
                    <WorksheetCard record={r} onDelete={() => onDelete(r.id)} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function WorksheetCard({
  record,
  primary = false,
  onDelete,
}: {
  record: WorksheetRecord;
  primary?: boolean;
  onDelete: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(record.imageBlob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [record.imageBlob]);

  async function downloadAgain() {
    let blob = record.pdfBlob;
    if (!blob) {
      // Rebuild a PDF on the fly from the saved image.
      const img = new Image();
      img.src = URL.createObjectURL(record.imageBlob);
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d')?.drawImage(img, 0, 0);
      blob = canvasToPdfBlob(canvas);
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${record.title}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div
      className={`rounded-2xl border bg-white p-3 ${
        primary ? 'border-accent/30 shadow-md shadow-accent/10' : 'border-ink/5'
      }`}
    >
      <div className="flex gap-3">
        {previewUrl && (
          <img
            src={previewUrl}
            alt={record.title}
            className="w-20 h-20 rounded-lg object-cover bg-cream flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink leading-snug break-words">{record.title}</p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-ink/55">
            {record.yearLevel && <span>{record.yearLevel}</span>}
            <span>Captured {formatDate(record.capturedAt)}</span>
            {record.downloadedAt && (
              <span className="text-accent/80">Downloaded {formatDateTime(record.downloadedAt)}</span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-ink/50">
            {record.answersCount} of {record.questionsCount} answered
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={downloadAgain}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-accent text-white text-sm font-medium hover:brightness-105 transition"
        >
          <IconShare size={14} /> Download PDF
        </button>
        <button
          onClick={onDelete}
          className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-ink/10 text-ink/50 hover:text-red-600 hover:border-red-300 transition"
          aria-label="Delete worksheet"
        >
          <IconTrash size={16} />
        </button>
      </div>
    </div>
  );
}

function SubjectIcon({ subject }: { subject: string }) {
  // Future: per-subject icons. For now, a generic doc icon for every subject.
  // Could differentiate maths (calculator), english (book), science (atom) etc.
  void subject;
  return <IconDoc size={18} />;
}
